import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useAllSearchParams } from 'storfox-route-hooks'
import { prop } from 'ramda'
import { useDeepCompareEffect } from 'storfox-api-hooks'
import { useSnackbar } from 'storfox-snackbar'

import { escapeAtob, unescapeBtoa } from '~/utils'
import { DashboardLayout } from '~/components/Layouts'
import { useNotification } from '~/components/Notification'
import * as NAV from '~/constants/nav-titles'
import * as ROUTES from '~/constants/routes'
import {
  SALE_ORDER_BULK_CREATE_COMPLETE,
  SALE_ORDER_BULK_CREATE_STATUS,
  SALE_ORDER_BULK_VALIDATE_COMPLETE,
  SALE_ORDER_BULK_VALIDATE_STATUS
} from '~/constants/notification-topics'
import { useProgressCard } from '~/components/ProgressCard'
import { useGoogleEvent } from '~/components/GoogleAnalytics/GoogleAnalytics'
import useMessages from '~/hooks/useMessages'

import { useSaleOrderImport } from '../hooks'
import SaleOrderImport from '../components/SaleOrderImport/SaleOrderImport'

function SaleOrderImportContainer () {
  const navigate = useNavigate()
  const snackbar = useSnackbar()
  const messages = useMessages()
  const saleOrderImport = useSaleOrderImport()
  const { payload } = useAllSearchParams()
  const { sendEvent } = useGoogleEvent()
  const progressCard = useProgressCard()

  const validateStatus = useNotification(SALE_ORDER_BULK_VALIDATE_STATUS)
  const validateComplete = useNotification(SALE_ORDER_BULK_VALIDATE_COMPLETE)
  const uploadComplete = useNotification(SALE_ORDER_BULK_CREATE_COMPLETE)
  const uploadStatus = useNotification(SALE_ORDER_BULK_CREATE_STATUS)

  const key = prop('key', escapeAtob(payload))

  useDeepCompareEffect(() => {
    if (key) {
      validateStatus.subscribe(key, ({ payload }) => {
        const result = prop('result', payload)
        const validated = prop('totalValidated', result)
        const total = prop('total', result)

        progressCard.handleValidationChange({ current: validated, total })
      })

      validateComplete.subscribe(key, ({ payload }) => {
        navigate(
          `${ROUTES.SALE_ORDER_IMPORT_PATH}?payload=${unescapeBtoa(payload)}`
        )

        validateComplete.setLoading(false)
      }, { replace: true })

      uploadStatus.subscribe(key, ({ payload }) => {
        const result = prop('result', payload)
        const completed = prop('totalCreated', result)
        const total = prop('total', result)
        if (!uploadComplete.isLoading) {
          uploadComplete.setLoading(true)
          progressCard.handleValidationChange({ current: total, total })
        }
        progressCard.handleCompletionChange({ current: completed, total })
      })

      uploadComplete.subscribe(key, () => {
        navigate(ROUTES.SALE_ORDER_LIST_PATH, { replace: true })
        uploadComplete.setLoading(false)
        sendEvent({ eventAction: 'Upload File Sale Order', eventCategory: 'Sale Order' })
      })
    }

    return () => {
      validateStatus.unsubscribe()
      validateComplete.unsubscribe()
      uploadStatus.unsubscribe()
      uploadComplete.unsubscribe()
    }
  }, [key])

  const handleSubmit = () => {
    uploadComplete.setLoading(true)
    return saleOrderImport.create({ key })
      .then(() => snackbar({ message: messages.IMPORT_REQUEST_SUCCESS }))
  }

  const handleSubscribe = key => {
    snackbar({ message: messages.NOTIFICATION_WAIT })
    validateComplete.setLoading(true)
    navigate(`${ROUTES.SALE_ORDER_IMPORT_PATH}?payload=${unescapeBtoa({ key })}`, { replace: true })
    sendEvent({ eventAction: 'Validate File Sale Order', eventCategory: 'Sale Order' })
  }

  const handleFileRemove = () => {
    validateComplete.unsubscribe()
    uploadComplete.unsubscribe()
    validateStatus.unsubscribe()
    uploadStatus.unsubscribe()
    progressCard.resetValues()
  }

  const title = 'Import orders'

  const isLoading = (
    saleOrderImport.isLoading ||
    validateComplete.isLoading ||
    uploadComplete.isLoading
  )

  const breadcrumbs = { title }

  return (
    <DashboardLayout
      title={title}
      activeNav={NAV.SALE_ORDERS}
      breadcrumbs={breadcrumbs}
    >
      <SaleOrderImport
        title={title}
        isLoading={isLoading}
        progressCard={progressCard}
        onSubscribe={handleSubscribe}
        onSubmit={handleSubmit}
        onFileRemove={handleFileRemove}
      />
    </DashboardLayout>
  )
}

export default SaleOrderImportContainer
